import { useState, useRef, useEffect } from "react";

const formatDuration = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  } else {
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
};

const formatClockTime = (timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  const hrs = date.getHours().toString().padStart(2, "0");
  const mins = date.getMinutes().toString().padStart(2, "0");
  return `${hrs}:${mins}`;
};

const VideoTimeline = ({
  startTime = 0, // Unix timestamp in seconds
  endTime = 0, // Unix timestamp in seconds
  maxMarkers = 20,
  currentTime = 0, // Unix timestamp in seconds
  onSeek = () => {},
  RTCSeek = () => {},
  children,
}) => {
  const progressRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const duration = Math.max(0, endTime - startTime);
  const relativeTime = currentTime - startTime;
  const progressPercent = duration > 0 ? (relativeTime / duration) * 100 : 0;

  // ⛳ Generate timeline markers
  const markers = [];
  const step = Math.max(1, Math.floor(duration / (maxMarkers - 1)));
  for (let t = startTime; t < endTime; t += step) {
    markers.push(t);
  }
  if (markers[markers.length - 1] !== endTime) {
    markers.push(endTime);
  }

  const handleSeek = (clientX) => {
    if (!progressRef.current || duration === 0) return;
    const rect = progressRef.current.getBoundingClientRect();
    let percent = (clientX - rect.left) / rect.width;
    percent = Math.max(0, Math.min(1, percent));
    const newTime = Math.round(startTime + percent * duration);
    onSeek(newTime);
  };

  const onMouseMove = (e) => {
    if (isDragging) handleSeek(e.clientX);
  };

  const onTouchMove = (e) => {
    if (isDragging && e.touches.length > 0) {
      handleSeek(e.touches[0].clientX);
    }
  };

  const endDrag = (e) => {
    if (!progressRef.current || duration === 0) return;
    const rect = progressRef.current.getBoundingClientRect();
    let percent = (e.clientX - rect.left) / rect.width;
    percent = Math.max(0, Math.min(1, percent));
    const newTime = Math.round(startTime + percent * duration);
    setIsDragging(false);
    RTCSeek(newTime);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", endDrag);
      window.addEventListener("touchmove", onTouchMove);
      window.addEventListener("touchend", endDrag);
    }
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", endDrag);
    };
  }, [isDragging]);

  return (
    <div className="timeline-wrapper">
      {/* Progress bar */}
      <div className="progress-strip">
        <div className="progress-line" ref={progressRef} onClick={(e) => handleSeek(e.clientX)}>
          <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
          <div
            className="seek-handle"
            style={{ left: `${progressPercent}%` }}
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}
            title={formatDuration(relativeTime)}
          >
            <div className="connector-line" />
          </div>
        </div>
      </div>

      {/* Children */}
      {children}

      {/* Timeline markers */}
      <div className="timeline-container">
        <div className="timeline">
          {markers.map((time, index) => (
            <div className="timeline-marker" key={index}>
              <div className="tick" />
              <div className="label">{formatClockTime(time)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VideoTimeline;
